<template>
    <div class="min-h-screen bg-gradient-to-br from-slate-100 via-white to-cyan-50 px-4 py-8">
        <div class="max-w-lg mx-auto bg-white px-6 py-8 shadow-xl rounded-2xl border border-white/80">
            <img src="../assets/logo.png" class="pr-6 pb-6 mx-auto w-48">

            <div v-if="loading" class="text-center text-sm text-gray-400 py-8">
                Carregando convite...
            </div>

            <div v-else-if="error" class="text-center py-6">
                <p class="text-red-500 font-semibold text-sm mb-2">{{ errorMessage }}</p>
                <router-link class="text-sm text-cyan-500 hover:underline" :to="{ name: 'login' }">Ir para o login</router-link>
            </div>

            <div v-else>
                <div class="text-center mb-6">
                    <h2 class="text-xl font-semibold text-gray-800 mb-1">Você foi convidado</h2>
                    <p class="text-sm text-gray-500">
                        Entre na conta <strong>{{ companyName }}</strong>
                    </p>
                    <p class="text-xs text-gray-400 mt-2">{{ inviteEmail }}</p>
                </div>

                <form @submit.prevent="acceptWithPassword" novalidate class="flex flex-col gap-y-4">
                    <div>
                        <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Seu nome</label>
                        <input v-model="form.name" type="text" maxlength="80" class="input input-bordered w-full text-sm" placeholder="Como você quer aparecer no sistema" />
                    </div>

                    <div>
                        <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Senha</label>
                        <input v-model="form.password" type="password" maxlength="80" class="input input-bordered w-full text-sm" placeholder="Crie sua senha" />

                        <div class="mt-3 rounded-xl border border-gray-100 bg-slate-50 p-3">
                            <div class="flex items-center justify-between gap-2 mb-2">
                                <span class="text-xs font-semibold uppercase tracking-wide text-gray-400">Força da senha</span>
                                <span class="text-xs font-semibold" :class="passwordStrength.valid ? 'text-emerald-600' : 'text-gray-500'">
                                    {{ passwordStrength.label }}
                                </span>
                            </div>
                            <div class="h-2 rounded-full bg-white overflow-hidden border border-gray-100">
                                <div class="h-full rounded-full transition-all duration-300" :class="passwordStrength.tone" :style="{ width: passwordStrength.width + '%' }"></div>
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                                <div
                                    v-for="item in passwordStrength.checks"
                                    :key="item.key"
                                    class="text-xs rounded-lg px-2 py-1.5 border"
                                    :class="item.met ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-white text-gray-500'"
                                >
                                    {{ item.met ? 'OK' : 'Falta' }}: {{ item.label }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label class="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Confirmar senha</label>
                        <input v-model="form.confirmPassword" type="password" maxlength="80" class="input input-bordered w-full text-sm" placeholder="Repita a senha" />
                    </div>

                    <div v-if="formError" class="text-red-500 text-xs">{{ formError }}</div>

                    <button
                        type="submit"
                        :disabled="submitting || !passwordStrength.valid"
                        class="mt-1 w-full btn btn-primary"
                    >
                        <svg v-if="submitting" class="animate-spin mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ submitting ? 'Criando conta...' : 'Criar conta' }}
                    </button>

                    <div v-if="googleClientId">
                        <div class="relative flex items-center my-3">
                            <div class="flex-grow border-t border-gray-200"></div>
                            <span class="mx-3 text-xs text-gray-400">ou</span>
                            <div class="flex-grow border-t border-gray-200"></div>
                        </div>
                        <button
                            type="button"
                            @click="acceptWithGoogle"
                            :disabled="submitting"
                            class="w-full btn btn-outline border-gray-300 hover:bg-gray-50 text-gray-700 gap-2"
                        >
                            <svg v-if="submitting" class="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <svg v-else viewBox="0 0 24 24" class="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                            Criar conta com Google
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AuthService from '../services/AuthService';
import HttpClient from '../services/HttpService';
import { getPasswordStrengthMeta } from '@/utils/password';

export default {
    name: 'AcceptInvite',
    setup() {
        const route = useRoute();
        const router = useRouter();
        const token = route.params.token;
        const googleClientId = process.env.VUE_APP_GOOGLE_CLIENT_ID || null;

        const loading = ref(true);
        const submitting = ref(false);
        const error = ref(false);
        const errorMessage = ref('');
        const inviteEmail = ref('');
        const companyName = ref('');
        const formError = ref('');
        const form = ref({ name: '', password: '', confirmPassword: '' });
        const passwordStrength = computed(() => getPasswordStrengthMeta(form.value.password));

        onMounted(async () => {
            try {
                const { data } = await HttpClient.get(`/auth/invite/${token}`);
                inviteEmail.value = data.data.email;
                companyName.value = data.data.companyName;
                form.value.name = data.data.name || '';
            } catch (err) {
                error.value = true;
                const code = err?.response?.data?.error;
                if (code === 'token_expired') {
                    errorMessage.value = 'Este convite expirou. Peça um novo convite ao administrador.';
                } else {
                    errorMessage.value = 'Convite inválido ou não encontrado.';
                }
            } finally {
                loading.value = false;
            }
        });

        async function acceptWithPassword() {
            formError.value = '';
            if (!form.value.name.trim()) {
                formError.value = 'Informe seu nome.';
                return;
            }
            if (!passwordStrength.value.valid) {
                formError.value = 'A senha ainda não atende todos os requisitos.';
                return;
            }
            if (form.value.password !== form.value.confirmPassword) {
                formError.value = 'As senhas não coincidem.';
                return;
            }

            try {
                submitting.value = true;
                const { data } = await HttpClient.post(`/auth/invite/${token}/accept`, {
                    name: form.value.name.trim(),
                    password: form.value.password,
                    device_id: AuthService.getOrCreateDeviceId(),
                    device_name: getDeviceName()
                });
                finishLogin(data.data);
            } catch (err) {
                const code = err?.response?.data?.error;
                if (code === 'token_expired') {
                    formError.value = 'Este convite expirou.';
                } else if (code === 'weak_password') {
                    formError.value = err?.response?.data?.text || 'Senha fora do padrão de segurança.';
                } else {
                    formError.value = 'Erro ao criar conta. Tente novamente.';
                }
            } finally {
                submitting.value = false;
            }
        }

        async function acceptWithGoogle() {
            if (!googleClientId) return;
            try {
                submitting.value = true;
                formError.value = '';
                const idToken = await getGoogleIdToken();
                if (!idToken) {
                    formError.value = 'Login com Google cancelado.';
                    return;
                }
                const { data } = await HttpClient.post(`/auth/invite/${token}/accept`, {
                    name: form.value.name.trim(),
                    google_token: idToken,
                    device_id: AuthService.getOrCreateDeviceId(),
                    device_name: getDeviceName()
                });
                finishLogin(data.data);
            } catch (err) {
                const code = err?.response?.data?.error;
                if (code === 'google_email_mismatch') {
                    formError.value = 'O email do Google não é o mesmo do convite.';
                } else {
                    formError.value = 'Erro ao autenticar com Google. Tente novamente.';
                }
            } finally {
                submitting.value = false;
            }
        }

        function finishLogin(data) {
            AuthService.storeLocalData({ ...data });
            router.push({ name: 'home' });
        }

        function getDeviceName() {
            const ua = navigator.userAgent;
            if (/iPhone|iPad|iPod/.test(ua)) return 'iPhone/iPad';
            if (/Android/.test(ua)) return 'Android';
            if (/Mac/.test(ua)) return 'Mac';
            if (/Windows/.test(ua)) return 'Windows';
            return 'Navegador';
        }

        function getGoogleIdToken() {
            return new Promise((resolve, reject) => {
                const run = () => {
                    window.google.accounts.id.initialize({
                        client_id: googleClientId,
                        callback: (response) => resolve(response?.credential || null),
                        auto_select: false,
                        cancel_on_tap_outside: true
                    });
                    window.google.accounts.id.prompt((notification) => {
                        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                            const btn = document.createElement('div');
                            document.body.appendChild(btn);
                            window.google.accounts.id.renderButton(btn, { type: 'standard' });
                            btn.querySelector('[role="button"]') && btn.querySelector('[role="button"]').click();
                            setTimeout(() => btn.remove(), 100);
                        }
                    });
                };

                if (window.google && window.google.accounts) {
                    run();
                } else {
                    const script = document.createElement('script');
                    script.src = 'https://accounts.google.com/gsi/client';
                    script.async = true;
                    script.onload = run;
                    script.onerror = reject;
                    document.head.appendChild(script);
                }
            });
        }

        return {
            loading,
            submitting,
            error,
            errorMessage,
            inviteEmail,
            companyName,
            form,
            formError,
            googleClientId,
            passwordStrength,
            acceptWithPassword,
            acceptWithGoogle
        };
    }
}
</script>
